import React from 'react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import { trackForgotPasswordValidationError, trackForgotPasswordSendFailure, trackForgotPasswordEmailSent } from 'pages/Auth/ResetPassword/analytics';
import {
    colors, space, fontSizes, lineHeights, fontWeights
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/ResetPassword/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/ResetPassword/locales/ResetPassword_fr_CA';
import { SCREENS, ERROR_CODES } from 'pages/Auth/ResetPassword/constants';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

/**
 * TRADITIONAL_RESET — SIM-18 / ACS-4355 / SUB-TASK-4.
 *
 * SIM-1 form body extracted as an inline screen (behavior verbatim):
 *   - AC-040 inline `role="alert"` when `errorCode === EMAIL_SEND_FAILED_CODE`.
 *   - `isSubmitting` double-submit guard (RP-3).
 *   - POS ERR_400004 → `showCheckYourEmailModal({ isOpen, email, isResetPasswordFlow })`
 *     — email is required (the modal reads props.email, no Redux fallback),
 *     matching the modal flow.
 *   - Generic → `showInfoModal(errorMsg)`.
 *   - On success → transition to EMAIL_SENT with `isPasskeyFlow=false`.
 */
const TraditionalResetForm = ({ onTransition, updateSharedState }) => {
    const dispatch = useDispatch();
    const emailInput = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendFailureMessage, setSendFailureMessage] = useState('');

    const isValid = () => !emailInput.current?.validateError();

    const dispatchShowInfoModal = errorMsg => {
        if (!errorMsg) {
            return;
        }

        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('pageTitle'),
                message: errorMsg,
                buttonText: getText('confirmButton'),
                bodyPaddingBottom: 4,
                isHtml: true
            })
        );
    };

    const submit = e => {
        e.preventDefault();

        if (!isValid()) {
            const emailError = emailInput.current?.validateError?.();
            trackForgotPasswordValidationError([emailError].filter(Boolean));

            return;
        }

        const email = emailInput.current.getValue();
        setSendFailureMessage('');
        setIsSubmitting(true);
        updateSharedState({ email, isPasskeyFlow: false });

        const successCallback = () => {
            setIsSubmitting(false);
            trackForgotPasswordEmailSent(email);
            onTransition(SCREENS.EMAIL_SENT, { email, isPasskeyFlow: false });
        };

        const failureCallback = response => {
            const errors = response?.errors || [];
            const errorCode = errors[0]?.errorCode;
            const errorMsg = response?.errorMessage || errors[0]?.errorMessage || getText('fallbackErrorMsg');

            flushSync(() => {
                setIsSubmitting(false);

                if (errorCode === ERROR_CODES.EMAIL_SEND_FAILED_CODE) {
                    setSendFailureMessage(getText('emailSendFailed'));
                }
            });

            trackForgotPasswordSendFailure(response?.errorMessages);

            if (errorCode === ERROR_CODES.EMAIL_SEND_FAILED_CODE) {
                return;
            }

            if (errorCode === ERROR_CODES.POS_ACCOUNT_ERROR) {
                // CheckYourEmailModal reads the address from props.email (no
                // Redux fallback), so it must be passed here — matching the
                // modal flow (ForgotPasswordModal.ctrlr.jsx:156).
                dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email, isResetPasswordFlow: true }));

                return;
            }

            dispatchShowInfoModal(errorMsg);
        };

        dispatch(UserActions.forgotPassword(email, successCallback, failureCallback));
    };

    return (
        <>
            <Text
                is='h1'
                fontSize={fontSizes.lg}
                fontWeight={fontWeights.bold}
                lineHeight={lineHeights.tight}
                marginBottom={4}
            >
                {getText('pageTitle')}
            </Text>

            <Text
                is='p'
                fontSize={fontSizes.md}
                textAlign='center'
                lineHeight={lineHeights.base}
                marginBottom={5}
            >
                {getText('pageDescription')}
            </Text>

            {sendFailureMessage ? (
                <Box
                    role='alert'
                    id='reset-email-error'
                    css={styles.failureAlert}
                    marginBottom={4}
                >
                    {sendFailureMessage}
                </Box>
            ) : null}

            <form
                noValidate
                onSubmit={submit}
            >
                <Box marginBottom={4}>
                    <InputEmail
                        label={getText('emailPlaceholder')}
                        ref={c => {
                            if (c !== null) {
                                emailInput.current = c;
                            }
                        }}
                        autoFocus={true}
                        hideAsterisk={true}
                    />
                </Box>

                <Button
                    type='submit'
                    width='100%'
                    variant='primary'
                    paddingY={space[3]}
                    disabled={isSubmitting}
                >
                    {getText('sendResetLinkButton')}
                </Button>
            </form>

            <Box marginTop={5}>
                <Text
                    fontSize={fontSizes.xs}
                    color={colors.gray}
                    lineHeight={lineHeights.base}
                    textAlign='center'
                >
                    <b>{getText('stillHavingTrouble')}</b>
                    <br />
                    {getText('unableToResetPassword')}{' '}
                    <Link
                        href='tel:1-877-737-4672'
                        color={colors.blue}
                        underline={true}
                    >
                        1-877-SEPHORA
                    </Link>{' '}
                    {getText('phoneNumber')}
                    {' | '}
                    {getText('forAssistance')}{' '}
                    <Link
                        color={colors.blue}
                        underline={true}
                        href='/beauty/accessibility'
                    >
                        {getText('accessibility')}
                    </Link>
                    .
                </Text>
            </Box>
        </>
    );
};

const styles = {
    failureAlert: {
        color: colors.red
    }
};

export default wrapFunctionalComponent(TraditionalResetForm, 'TraditionalResetForm');
